import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { Text } from '@fhs/ui';
import ConfirmDialog from 'components/confirm-dialog';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from 'state/crm-events';
import {
  ClickEventComponentNames,
  DialogViewComponentNames,
} from 'state/crm-events/constants';
import { useOrderContext } from 'state/order';

type SwitchServiceModeDialogProps = {
  showDialog: boolean;
  onConfirm?: () => void;
  onDismiss: () => void;
  // default is FALSE
  // since the default action should be switching between store locations
  switchingFromPickUpToDelivery?: boolean;
};

export const SwitchServiceModeDialog = ({
  showDialog,
  onConfirm = () => {},
  onDismiss,
  // if TRUE
  // this should be displayed from 'delivery-address-form-footer.tsx'
  // at the moment the user chooses to go from pick up to delivery
  switchingFromPickUpToDelivery = false,
}: SwitchServiceModeDialogProps) => {
  const { formatMessage } = useIntl();
  const { emptyCart } = useOrderContext();
  const { logDialogView, logRBIEvent } = useCRMEventsContext();
  const hasLoggedDialogViewRef = useRef(false);

  const confirmationTitle = switchingFromPickUpToDelivery
    ? formatMessage({ id: 'changeServiceModeToDeliveryConfirmationTitle' })
    : formatMessage({ id: 'changeServiceModeConfirmationTitle' });
  const confirmLabel = formatMessage({ id: 'changeServiceModeConfirmationConfirm' });
  const cancelLabel = formatMessage({ id: 'cancel' });

  useEffect(() => {
    if (!showDialog) {
      hasLoggedDialogViewRef.current = false;
      return;
    }

    if (hasLoggedDialogViewRef.current) {
      return;
    }

    hasLoggedDialogViewRef.current = true;
    logDialogView(confirmationTitle as DialogViewComponentNames);
  }, [showDialog, confirmationTitle, logDialogView]);

  const logDialogClick = useCallback(
    (action: string) => {
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          text: action,
          module: confirmationTitle,
        },
      });
    },
    [confirmationTitle, logRBIEvent]
  );

  const handleDismiss = useCallback(() => {
    logDialogClick(cancelLabel);
    onDismiss();
  }, [cancelLabel, logDialogClick, onDismiss]);

  const handleChangeServiceModeConfirmation = useCallback(() => {
    logDialogClick(confirmLabel);
    emptyCart();
    onConfirm();
    onDismiss();
  }, [confirmLabel, emptyCart, logDialogClick, onConfirm, onDismiss]);

  return (
    <ConfirmDialog
      showDialog={showDialog}
      onConfirm={handleChangeServiceModeConfirmation}
      onDismiss={handleDismiss}
      onCancel={handleDismiss}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      ariaLabel={confirmationTitle}
      modalAppearanceEventMessage={confirmationTitle}
      headingComponent={<Text.Heading type="one">{confirmationTitle}</Text.Heading>}
      bodyComponent={
        <Text style={styles.dialogMessage}>
          {formatMessage({ id: 'changeServiceModeConfirmationMessage' })}
        </Text>
      }
    />
  );
};

const styles = StyleSheet.create({
  dialogMessage: {
    marginVertical: 16,
    lineHeight: 24,
  },
});
